import {Injectable} from '@angular/core';
import {TranslocoService} from '@jsverse/transloco';
import {Utils, OnAppInitInterface} from '@astek/utils';
import {DEFAULT_LANGUAGE, LanguagesEnum} from './languages.enum';
import {DateAdapter} from '@angular/material/core';

@Injectable()
export class LanguageService implements OnAppInitInterface {

  private static readonly LANGUAGE_LOCAL_STORAGE_KEY: string = 'language';

  constructor(
    private readonly _translocoService: TranslocoService,
    private readonly _dateAdapter: DateAdapter<Date>,
  ) {
  }

  public get availableLanguages(): Array<string> {
    return this._translocoService.getAvailableLangs().map(lang => {
      if (Utils.isString(lang)) {
        return lang;
      }
      return lang.id;
    });
  }

  public get navigatorLanguage(): string {
    let language = navigator.language;
    if (language.includes('-')) {
      language = language.substring(0, navigator.language.indexOf('-'));
    }
    return language;
  }

  public get activeLanguage(): string {
    return this._translocoService.getActiveLang();
  }

  public onAppInit(): void {
    this._translocoService.setAvailableLangs(Object.values(LanguagesEnum));
    this._translocoService.setDefaultLang(DEFAULT_LANGUAGE);
    this._translocoService.setFallbackLangForMissingTranslation({fallbackLang: DEFAULT_LANGUAGE});

    const savedLanguage: string | null = this.readLanguageFromLocalStorage();
    if (Utils.isNotNull(savedLanguage)) {
      console.info('Local storage language:', savedLanguage);
      this.selectLanguage(savedLanguage);
    } else {
      console.info('Navigator language:', this.navigatorLanguage);
      this.selectLanguage(this.navigatorLanguage);
    }
  }

  public selectLanguage(language: string): void {
    let requestedLanguage = language;
    if (!this.availableLanguages.includes(requestedLanguage)) {
      console.warn(`Language '${requestedLanguage}' is not supported, falling back to ${DEFAULT_LANGUAGE}`);
      requestedLanguage = DEFAULT_LANGUAGE;
    }
    this._translocoService.setActiveLang(requestedLanguage);
    console.info(`Using language '${this._translocoService.getActiveLang()}'.`);
    document.documentElement.setAttribute('lang', this._translocoService.getActiveLang());
    this._dateAdapter.setLocale(requestedLanguage);
    this.writeLanguageToLocalStorage(requestedLanguage);
  }

  public readLanguageFromLocalStorage(): string | null {
    return localStorage.getItem(LanguageService.LANGUAGE_LOCAL_STORAGE_KEY);
  }

  public writeLanguageToLocalStorage(language: string): void {
    return localStorage.setItem(LanguageService.LANGUAGE_LOCAL_STORAGE_KEY, language);
  }
}
